/* eslint-disable @typescript-eslint/promise-function-async */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import * as R from "remeda";
import type { CandidateStore } from "../stores/automation/candidate.store";
import type { ActionCandidate } from "../types/candidate";
import {
  Availability,
  type IntegrationDescription,
} from "@smartsuite/automation-engine-api";
import type { EngineClient } from "./engineClient";
import * as T from "../stores/automation/mergers/trigger";
import * as A from "../stores/automation/mergers/actions";
import type { AutomationsStore } from "../stores/automation/index.store";
import { isTruthy } from "remeda";
import type { IntegrationStore } from "../stores/integration.store";
import { locateActionByHash } from "../stores/automation/utils/action-group-walk";

export const updateTriggerDescription = async (
  candidate: CandidateStore,
  conditionListCleanup: boolean,
  engineClient: EngineClient,
  automations: AutomationsStore,
  integrations: IntegrationStore,
): Promise<void> => {
  if (
    automations.isAllDynamicTriggerDescriptionsLoading() ||
    !isTruthy(automations.candidate.self.trigger.credentialId)
  ) {
    return; // Prevent multiple requests if already loading
  }

  return await new Promise((resolve, reject) => {
    automations.addToLoadingDynamicDescriptionProcess("trigger");
    void engineClient
      .getDynamicTriggerDescription({
        trigger: candidate.self.trigger,
        solutionId: candidate.self.solutionId,
        automationId: candidate.self.automationId,
      })
      .then(({ trigger: description, integrationState }) => {
        const integration = integrations.list.find(
          (integration) =>
            integration.integrationId ===
            candidate.self.trigger.triggerReference?.integrationId,
        );
        let isIntegrationDisabled = false;
        if (
          integration !== undefined &&
          integration.info?.availability === Availability.AVAILABILITY_DISABLED
        ) {
          isIntegrationDisabled = true;
        }
        candidate.setTrigger(
          T.mergeInputDescriptionList(
            description!.inputs,
            conditionListCleanup,
          ),
          T.mergeTriggerDescription(description!),
          T.mergeIntegrationState(integrationState),
          T.mergeTriggerDisabledError(description!),
          T.mergeTriggerIntegrationDisabledError(
            description!,
            isIntegrationDisabled,
          ),
        );
        automations.removeFromLoadingDynamicDescriptionProcess("trigger");
        resolve();
      })
      .catch((error) => {
        automations.removeFromLoadingDynamicDescriptionProcess("trigger");
        reject(error);
      });
  });
};

export const updateActionDescription = async (
  candidate: CandidateStore,
  action: ActionCandidate,
  integrationList: IntegrationDescription[],
  engineClient: EngineClient,
  automations: AutomationsStore,
): Promise<void> => {
  return await new Promise((resolve) => {
    automations.addToLoadingDynamicDescriptionProcess(
      "action",
      action.actionReference?.instanceId,
    );
    void engineClient
      .getDynamicActionDescription({
        action: R.find(candidate.allActions, (a) => a.hash === action.hash),
        solutionId: candidate.self.solutionId,
        automationId: candidate.self.automationId,
      })
      .then(({ action: description, integrationState }) => {
        const staticInputList = R.pipe(
          integrationList,
          R.find(
            (i) => i.integrationId === action.actionReference!.integrationId,
          ),
          (integration) =>
            R.find(
              integration?.actions ?? [],
              (a) => a.actionId === action.actionReference!.actionId,
            ),
          (a) => a?.inputs ?? [],
        );
        const dynamicInputList = description?.inputs ?? [];
        // Write the fetched description back through the action's OWN nearest
        // container (root / loop / branch). Addressing it by the TOP-LEVEL
        // group type missed actions nested inside a branch: the writeback
        // routed by `currentGroupLocator`, which isn't set to that branch
        // during the bulk description load on open, so it no-op'd and the
        // action's settings hung on the loading skeleton forever (Task 5.5).
        const locator = locateActionByHash(
          candidate.self.actionGroups,
          action.hash,
        );

        const integration = integrationList.find(
          (i) => i.integrationId === action.actionReference!.integrationId,
        );
        let isIntegrationDisabled = false;
        if (
          integration !== undefined &&
          integration.info?.availability === Availability.AVAILABILITY_DISABLED
        ) {
          isIntegrationDisabled = true;
        }

        const actionDescription = integration?.actions.find(
          (a) => a.actionId === action.actionReference!.actionId,
        );
        let isActionDisabled = false;
        if (
          actionDescription !== undefined &&
          actionDescription.info?.availability ===
            Availability.AVAILABILITY_DISABLED
        ) {
          isActionDisabled = true;
        }

        const mergedInputs = A.mergeStaticAndDynamicInputDescriptions(
          staticInputList,
          dynamicInputList,
        );

        if (locator !== undefined) {
          candidate.setActionsAt(locator, [
            A.mergeActionDescription(action.hash, {
              ...description!,
              inputs: mergedInputs,
            }),
            A.mergeInputDescriptionList(action.hash, mergedInputs),
            A.mergeActionIntegrationState(action.hash, integrationState),
            A.mergeActionDeletedError(action.hash, false),
            A.mergeActionAccessError(action.hash, false),
            ...(isActionDisabled
              ? [A.mergeActionDisabledError(action.hash, true)]
              : []),
            ...(isIntegrationDisabled
              ? [A.mergeIntegrationDisabledError(action.hash, true)]
              : []),
          ]);
        }
        candidate.setShowErrorSection(false);
      })
      .catch((error) => {
        checkActionDescriptionError(action, candidate, error);
      })
      .finally(() => {
        automations.removeFromLoadingDynamicDescriptionProcess(
          "action",
          action.actionReference?.instanceId,
        );
        resolve();
      });
  });
};

export const checkActionDescriptionError = (
  action: ActionCandidate,
  candidate: CandidateStore,
  /* eslint-disable  @typescript-eslint/no-explicit-any */
  error: any,
): void => {
  // Route by the action's own nearest container so a not-found / no-access
  // action nested in a branch (or loop) is reset in place, same as the success
  // path above.
  const locator = locateActionByHash(candidate.self.actionGroups, action.hash);
  switch (error.code) {
    case "not_found": {
      // reset action
      if (locator !== undefined) {
        candidate.setActionsAt(locator, [
          A.resetAction(action.hash),
          A.mergeActionDeletedError(action.hash, true),
        ]);
      }
      break;
    }
    case "permission_denied": {
      if (locator !== undefined) {
        candidate.setActionsAt(locator, [
          A.mergeActionAccessError(action.hash, true),
        ]);
      }
      break;
    }
    default: {
      console.error("Error on get action description", error);
    }
  }
};
